import { useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { IconChevronDown as ChevronDown, IconChevronRight as ChevronRight } from '@tabler/icons-react';
import { Dropdown, DropdownTrigger, DropdownMenu, DropdownItem } from '@heroui/react';
import Trash from '@/icons/Trash';
import EditIcon from '@/icons/EditIcon';
import { fetchTierSummary } from '@/apiEndpoints';
import { Select, SelectItem } from '@/components/ui/AppSelect';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import useIsMobile from '@/hooks/useIsMobile';
import {
    formatRange,
    groupByTicket,
    groupByTier,
    type Group,
    type StatLookup,
    type TierInfo,
    type TierPrice,
} from '@/utils/tierPricing';

interface PanelTicket {
    _id: string;
    name: string;
    tierPrices?: TierPrice[];
    tierIds?: string[];
    status?: boolean;
    saleStart?: string;
    saleEnd?: string;
}

const formatSaleDate = (value?: string): string => {
    const m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/.exec((value ?? '').trim());
    if (!m) return '';
    const date = new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    if (!m[4]) return date;
    const time = new Date(2000, 0, 1, +m[4], +m[5]).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    return `${date}, ${time}`;
};

const formatSaleRange = (ticket?: PanelTicket): string => {
    const start = formatSaleDate(ticket?.saleStart);
    const end = formatSaleDate(ticket?.saleEnd);
    if (start && end) return `${start} - ${end}`;
    if (start) return `From ${start}`;
    if (end) return `Until ${end}`;
    return '';
};

interface Props {
    eventId: string;
    userId?: string;
    tickets: PanelTicket[];
    currencySymbol: string;
    canEdit: boolean;
    onAddTicket: () => void;
    onEditTicket: (ticketId: string) => void;
    onDeleteTicket: (ticketId: string) => void;
}

type GroupBy = 'ticket' | 'tier';

const DeactivatedTag = () => (
    <span className="inline-flex shrink-0 items-center rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-semibold text-red-600 dark:bg-red-500/15 dark:text-red-400">Deactivated</span>
);

export default function TierPricingPanel({
    eventId, userId, tickets, currencySymbol, canEdit, onAddTicket, onEditTicket, onDeleteTicket,
}: Props) {
    const [groupBy, setGroupBy] = useLocalStorage<GroupBy>(`ticketGroupBy:${userId ?? 'anon'}:${eventId}`, 'ticket');
    const isMobile = useIsMobile();
    const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

    const { data: summary, isLoading, isError, refetch } = useQuery({
        queryKey: ['tierSummary', eventId, tickets.map(t => `${t._id}:${(t.tierIds ?? []).join(',')}:${(t.tierPrices ?? []).map(p => `${p.tier}=${p.price}`).join(',')}`).join('|')],
        queryFn: () => fetchTierSummary(eventId),
        enabled: Boolean(eventId),
        placeholderData: keepPreviousData,
    });

    const fmt = (n: number) => `${currencySymbol}${n.toFixed(2)}`;
    const tiers: TierInfo[] = (summary?.tiers ?? []).map((t: any) => ({ name: t.name, color: t.color, seatCount: t.seatCount }));

    const stat: StatLookup = useMemo(() => {
        const map = new Map<string, { sold: number; capacity: number; revenue: number }>();
        (summary?.tickets ?? []).forEach((t: any) =>
            t.tiers.forEach((r: any) => map.set(`${t.ticketId}:${r.tier}`, { sold: r.sold, capacity: r.capacity, revenue: r.revenue })));
        return (ticketId, tier) => {
            const s = map.get(`${ticketId}:${tier}`);
            return s ? { ticketId, ...s } : undefined;
        };
    }, [summary]);

    const priced: PanelTicket[] = useMemo(() => tickets.map(t => {
        const rows = (summary?.tickets ?? []).find((x: any) => x.ticketId === t._id)?.tiers;
        return rows ? { ...t, tierPrices: rows.filter((r: any) => r.price !== '').map((r: any) => ({ tier: r.tier, price: r.price })) } : t;
    }), [tickets, summary]);
    const groups: Group[] = useMemo(
        () => (groupBy === 'tier' ? groupByTier(priced, tiers, stat) : groupByTicket(priced, stat)),
        [groupBy, priced, tiers, stat],
    );

    const noSeatEntries = useMemo(() => (summary?.tickets ?? []).flatMap((t: any) =>
        (t.noSeat ?? []).map((n: any) => ({
            ticketId: t.ticketId as string,
            ticketName: (tickets.find(x => x._id === t.ticketId)?.name ?? '') as string,
            tier: (n.tier ?? null) as string | null,
            quantity: Number(n.quantity || 0),
        }))), [summary, tickets]);

    const ticketById = useMemo(() => new Map(tickets.map(t => [t._id, t])), [tickets]);

    const deactivatedIds = useMemo(() => new Set(tickets.filter(t => t.status === false).map(t => t._id)), [tickets]);

    const tierByName = Object.fromEntries((summary?.tiers ?? []).map((t: any) => [t.name, t]));

    const groupRange = (g: Group) =>
        g.rows.length === 0 ? '' : formatRange(g.rows.map(r => ({ tier: r.tier, price: r.price })), fmt);

    const actions = (ticketId: string) => canEdit && (isMobile ? (
        <Dropdown placement="bottom-end">
            <DropdownTrigger>
                <button
                    type="button"
                    aria-label="Ticket actions"
                    className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-xl leading-none text-grey30 hover:bg-grey99 dark:text-nightTextSecondary dark:hover:bg-white/[0.06]"
                >
                    ⋮
                </button>
            </DropdownTrigger>
            <DropdownMenu aria-label="Ticket actions">
                <DropdownItem key="edit" onPress={() => onEditTicket(ticketId)}>Edit</DropdownItem>
                <DropdownItem key="delete" className="text-danger" color="danger" onPress={() => onDeleteTicket(ticketId)}>Delete</DropdownItem>
            </DropdownMenu>
        </Dropdown>
    ) : (
        <span className="flex items-center gap-2">
            <button
                type="button"
                title="Edit"
                aria-label="Edit ticket"
                className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-grey99 text-grey20 transition-colors hover:bg-grey95 dark:bg-white/[0.06] dark:text-nightTextSecondary dark:hover:bg-white/10"
                onClick={() => onEditTicket(ticketId)}
            >
                <EditIcon />
            </button>
            <button
                type="button"
                title="Delete"
                aria-label="Delete ticket"
                className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-red-50 text-red-500 transition-colors hover:bg-red-100 dark:bg-red-500/10 dark:text-red-400 dark:hover:bg-red-500/20"
                onClick={() => onDeleteTicket(ticketId)}
            >
                <Trash />
            </button>
        </span>
    ));

    return (
        <div className="mb-8 flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex items-center gap-2 text-sm text-[#222] dark:text-nightTextSecondary">
                    <span className="whitespace-nowrap">Group by</span>
                    <Select
                        aria-label="Group by"
                        className="w-44"
                        selectedKeys={[groupBy]}
                        disallowEmptySelection
                        onSelectionChange={keys => {
                            const value = Array.from(keys)[0] as GroupBy | undefined;
                            if (value) setGroupBy(value);
                        }}
                    >
                        <SelectItem key="ticket">Ticket type</SelectItem>
                        <SelectItem key="tier">Tier</SelectItem>
                    </Select>
                </div>
                <div className="sm:ml-auto flex flex-col sm:flex-row gap-3">
                    <button
                        type="button"
                        onClick={onAddTicket}
                        className="h-10 px-4 rounded bg-primary40 text-white text-sm whitespace-nowrap"
                    >
                        Add New Ticket
                    </button>
                </div>
            </div>

            {isError && (
                <div role="alert" className="flex items-center justify-between rounded border border-danger p-3 text-sm">
                    <span>Could not load tier data.</span>
                    <button type="button" className="underline" onClick={() => refetch()}>Retry</button>
                </div>
            )}

            {isLoading && !summary ? (
                <div className="flex flex-col gap-3" aria-busy="true">
                    {[0, 1, 2].map(i => <div key={i} className="h-14 rounded bg-gray-100 dark:bg-nightElevated animate-pulse" />)}
                </div>
            ) : tickets.length === 0 ? (
                <div className="text-center py-12 border border-dashed border-grey90 dark:border-nightBorder rounded">
                    <p className="text-sm text-grey30 dark:text-nightMuted mb-3">No tickets yet.</p>
                    <button type="button" className="h-10 px-4 rounded bg-primary40 text-white text-sm whitespace-nowrap" onClick={onAddTicket}>
                        Create your first ticket
                    </button>
                </div>
            ) : (
                <ul className="flex flex-col gap-3">
                    {groups.map(g => {
                        const isCollapsed = collapsed[g.key] === true;
                        const missingTiers = groupBy === 'ticket'
                            ? tiers.filter(t => !g.rows.some(r => r.tier === t.name))
                            : [];
                        return (
                            <li key={g.key} className="rounded bg-white dark:bg-nightElevated">
                                <div className="flex items-center gap-3 pr-4">
                                <button
                                    type="button"
                                    className="flex w-full items-center gap-3 p-4 text-left"
                                    aria-expanded={!isCollapsed}
                                    onClick={() => setCollapsed(c => ({ ...c, [g.key]: !isCollapsed }))}
                                >
                                    {isCollapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                                    {g.color && <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: g.color }} aria-hidden />}
                                    <span className="font-medium truncate text-[#222] dark:text-nightTextSecondary" title={g.label}>{g.label}</span>
                                    {groupBy === 'ticket' && deactivatedIds.has(g.key) && <DeactivatedTag />}
                                    <span className="ml-auto flex items-center gap-4 text-sm whitespace-nowrap text-gray-400 dark:text-nightMuted">
                                        {groupBy === 'ticket' && formatSaleRange(ticketById.get(g.key)) && (
                                            <span className="hidden sm:inline text-xs">{formatSaleRange(ticketById.get(g.key))}</span>
                                        )}
                                        <span>{groupRange(g)}</span>
                                    </span>
                                </button>
                                {groupBy === 'ticket' && actions(g.key)}
                                </div>
                                {!isCollapsed && (
                                    <ul className="border-t border-grey90 dark:border-nightBorder">
                                        {g.rows.map(r => {
                                            const soldOut = r.capacity > 0 && r.sold >= r.capacity;
                                            const label = groupBy === 'tier' ? r.ticketName : r.tier;
                                            const color = groupBy === 'ticket' ? tierByName[r.tier]?.color : undefined;
                                            return (
                                                <li key={`${r.ticketId}:${r.tier}`} className="flex items-center gap-3 px-4 py-3 text-sm">
                                                    {color && <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: color }} aria-hidden />}
                                                    <button
                                                        type="button"
                                                        className="truncate text-left hover:underline"
                                                        title={label}
                                                        onClick={() => onEditTicket(r.ticketId)}
                                                    >
                                                        {label}
                                                    </button>
                                                    {groupBy === 'tier' && deactivatedIds.has(r.ticketId) && <DeactivatedTag />}
                                                    <span className="ml-auto flex items-center gap-4 whitespace-nowrap">
                                                        {groupBy === 'tier' && formatSaleRange(ticketById.get(r.ticketId)) && (
                                                            <span className="hidden sm:inline text-xs text-gray-400 dark:text-nightMuted">{formatSaleRange(ticketById.get(r.ticketId))}</span>
                                                        )}
                                                        {soldOut && <span className="text-xs font-semibold text-danger">Sold out</span>}
                                                        <span>{r.price === 0 ? 'Free' : fmt(r.price)}</span>
                                                        <span>{r.sold}/{r.capacity}</span>
                                                        {groupBy === 'tier' && actions(r.ticketId)}
                                                    </span>
                                                </li>
                                            );
                                        })}
                                        {noSeatEntries
                                            .filter((n: any) => groupBy === 'ticket' ? n.ticketId === g.key : n.tier === g.key)
                                            .map((n: any) => (
                                                <li key={`noseat:${n.ticketId}:${n.tier}`} className="flex items-center gap-3 px-4 py-3 text-sm">
                                                    <span className="truncate">{groupBy === 'tier' ? `${n.ticketName} (no seat assigned)` : `${n.tier ?? 'No tier'} (no seat assigned)`}</span>
                                                    <span className="ml-auto whitespace-nowrap">{n.quantity} sold</span>
                                                </li>
                                            ))}
                                        {missingTiers.map(t => (
                                            <li key={`missing:${t.name}`} className="flex items-center gap-3 px-4 py-3 text-sm text-gray-400 dark:text-nightMuted">
                                                <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: t.color }} aria-hidden />
                                                <span className="truncate" title={t.name}>{t.name}</span>
                                                <span className="ml-auto flex items-center gap-3 whitespace-nowrap">
                                                    Not sold in this tier
                                                    {canEdit && (
                                                        <button type="button" className="underline" onClick={() => onEditTicket(g.key)}>Add price</button>
                                                    )}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </li>
                        );
                    })}
                    {groupBy === 'tier' && noSeatEntries.some((n: any) => !n.tier) && (
                        <li className="rounded bg-white dark:bg-nightElevated">
                            <div className="p-4 font-medium text-[#222] dark:text-nightTextSecondary">No seat or tier assigned</div>
                            <ul className="border-t border-grey90 dark:border-nightBorder">
                                {noSeatEntries.filter((n: any) => !n.tier).map((n: any) => (
                                    <li key={`noseat:${n.ticketId}`} className="flex items-center gap-3 px-4 py-3 text-sm">
                                        <span className="truncate">{n.ticketName}</span>
                                        <span className="ml-auto whitespace-nowrap">{n.quantity} sold</span>
                                    </li>
                                ))}
                            </ul>
                        </li>
                    )}
                </ul>
            )}

        </div>
    );
}
